import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useToast } from "@/hooks/use-toast";
import {
  LeadContact,
  fetchContactsPaginated,
  deleteLeadContact
} from "@/services/leadContactsService";

const DEFAULT_PAGE_SIZE = 50;

export const useContactsData = () => {
  const [contacts, setContacts] = useState<LeadContact[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [componentError, setComponentError] = useState<boolean>(false);

  // Server-side pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [totalItems, setTotalItems] = useState(0);

  const { toast } = useToast();
  const loadingRef = useRef(false);
  const requestId = useRef(0);

  // Debounce the search input before hitting the API
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery.trim()), 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Reset to first page whenever server-side filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, statusFilter]);

  const loadContacts = useCallback(async () => {
    const id = ++requestId.current;
    loadingRef.current = true;
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetchContactsPaginated({
        page: currentPage,
        per_page: pageSize,
        search: debouncedSearch || undefined,
        status: statusFilter && statusFilter.toLowerCase() !== "all" ? statusFilter : undefined
      });

      // Ignore stale responses
      if (id !== requestId.current) return;

      setContacts(response.items);
      setTotalItems(response.total_count);
    } catch (err) {
      if (id !== requestId.current) return;
      console.error("📞 useContactsData: Error fetching contacts:", err);
      const errorMessage = err instanceof Error ? err.message : "Failed to load contacts";
      setError(errorMessage);
      setContacts([]);
      setTotalItems(0);
      toast({
        title: "Error",
        description: "Could not load contacts. Please try again.",
        variant: "destructive"
      });
    } finally {
      if (id === requestId.current) {
        setIsLoading(false);
        loadingRef.current = false;
      }
    }
  }, [currentPage, pageSize, debouncedSearch, statusFilter, toast]);

  useEffect(() => {
    loadContacts();
  }, [loadContacts]);

  // Departments present in the current page (server has no department filter)
  const departments = useMemo(() => {
    const deptSet = new Set<string>();
    contacts.forEach(contact => {
      if (contact.department) deptSet.add(contact.department);
    });
    return Array.from(deptSet);
  }, [contacts]);

  // Department filtering stays client-side over the current page
  const filteredContacts = useMemo(() => {
    try {
      if (!departmentFilter || departmentFilter.toLowerCase() === "all") return contacts;
      return contacts.filter(contact => contact.department === departmentFilter);
    } catch (err) {
      console.error("📞 useContactsData: Error filtering contacts:", err);
      setComponentError(true);
      return contacts;
    }
  }, [contacts, departmentFilter]);

  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(Math.max(1, page));
  }, []);

  const handlePageSizeChange = useCallback((newPageSize: number) => {
    setPageSize(Math.min(100, Math.max(1, newPageSize)));
    setCurrentPage(1);
  }, []);

  const handleDeleteContact = async (contactId: string | number) => {
    try {
      await deleteLeadContact(contactId.toString());
      toast({
        title: "Contact deleted",
        description: "The contact has been successfully deleted."
      });
      setContacts(prev => prev.filter(c => c.id !== contactId));
      setTotalItems(prev => Math.max(0, prev - 1));
      return true;
    } catch (err) {
      console.error("Error deleting contact:", err);
      toast({
        title: "Error",
        description: "Could not delete the contact. Please try again later.",
        variant: "destructive"
      });
      return false;
    }
  };

  const handleContactUpdated = async () => {
    try {
      await loadContacts();
      toast({
        title: "Contact updated",
        description: "The contact has been successfully updated."
      });
    } catch (error) {
      console.error("Error reloading contacts after update:", error);
      toast({
        title: "Update completed",
        description: "Contact updated but failed to refresh list. Please refresh manually.",
        variant: "destructive"
      });
    }
  };

  return {
    contacts,
    filteredContacts,
    searchQuery,
    setSearchQuery,
    departmentFilter,
    setDepartmentFilter,
    statusFilter,
    setStatusFilter,
    departments,
    isLoading,
    error,
    componentError,
    handleDeleteContact,
    handleContactUpdated,
    loadContacts,
    // Server-side pagination
    currentPage,
    pageSize,
    totalItems,
    totalPages,
    handlePageChange,
    handlePageSizeChange
  };
};
